import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { api, errText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Palette, Upload, RotateCcw, Save } from "lucide-react";

const toText = (arr) => (Array.isArray(arr) ? arr.join("\n") : arr || "");
const toLines = (str) => (str || "").split("\n").map((s) => s.trim()).filter(Boolean);

export default function Settings() {
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/settings/branding")
      .then((r) => setB(r.data.branding))
      .catch(() => toast.error("Could not load branding"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        store_name: b.store_name || "",
        header_lines: toLines(b.header_lines_text ?? toText(b.header_lines)),
        company_lines: toLines(b.company_lines_text ?? toText(b.company_lines)),
        doc_label: b.doc_label || "",
        page2_lines: toLines(b.page2_lines_text ?? toText(b.page2_lines)),
        terms_heading: b.terms_heading || "",
        footer_terms: toLines(b.footer_terms_text ?? toText(b.footer_terms)),
        closing: b.closing || "",
      };
      const r = await api.put("/settings/branding", payload);
      setB(r.data.branding);
      toast.success("Branding saved — new quotes will use it");
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/branding/logo", fd);
      setB((prev) => ({ ...prev, logo_data_url: r.data.logo_data_url }));
      toast.success("Logo updated");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail) || "Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetLogo = async () => {
    try {
      const r = await api.delete("/settings/branding/logo");
      setB((prev) => ({ ...prev, logo_data_url: r.data.logo_data_url }));
      toast.success("Logo reset to default");
    } catch (e) {
      toast.error("Could not reset logo");
    }
  };

  const field = (key) => b?.[`${key}_text`] ?? toText(b?.[key]);
  const setField = (key, val) => setB((prev) => ({ ...prev, [`${key}_text`]: val }));

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="mx-auto max-w-3xl px-6 py-6">
        <div className="mb-5 flex items-center gap-2">
          <Palette className="h-5 w-5 text-slate-400" />
          <h1 className="font-head text-2xl font-600 tracking-tight text-slate-900">Quote PDF branding</h1>
        </div>

        {loading || !b ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-6 rounded-lg border border-slate-200 bg-white p-6">
            <div className="space-y-2">
              <Label>Store logo</Label>
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-56 items-center justify-center rounded-md border border-slate-200 bg-slate-50 p-2">
                  {b.logo_data_url ? (
                    <img src={b.logo_data_url} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                  ) : (
                    <span className="text-xs text-slate-400">No logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
                  <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn" className="gap-1.5">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
                  </Button>
                  <Button variant="ghost" size="sm" onClick={resetLogo} data-testid="reset-logo-btn" className="gap-1.5 text-slate-500">
                    <RotateCcw className="h-3.5 w-3.5" /> Reset to default
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Store name (fallback if no logo)</Label>
              <Input value={b.store_name || ""} onChange={(e) => setB({ ...b, store_name: e.target.value })} data-testid="store-name" />
            </div>

            <div className="space-y-1.5">
              <Label>Store address / phone / email</Label>
              <p className="text-xs text-slate-400">Pulled automatically from each store in Lightspeed and shown per quote. The lines below are only a fallback if a store has no address on file.</p>
              <Textarea rows={3} value={field("header_lines")} onChange={(e) => setField("header_lines", e.target.value)} data-testid="header-lines" />
            </div>

            <div className="space-y-1.5">
              <Label>Shared header lines <span className="text-slate-400">(GST, bank, website — same on every quote)</span></Label>
              <Textarea rows={3} value={field("company_lines")} onChange={(e) => setField("company_lines", e.target.value)} data-testid="company-lines" />
            </div>

            <div className="space-y-1.5">
              <Label>Document label</Label>
              <Input value={b.doc_label || ""} onChange={(e) => setB({ ...b, doc_label: e.target.value })} data-testid="doc-label" />
            </div>

            <div className="space-y-1.5">
              <Label>Page 2 header lines <span className="text-slate-400">(warranty / delivery contact)</span></Label>
              <Textarea rows={3} value={field("page2_lines")} onChange={(e) => setField("page2_lines", e.target.value)} data-testid="page2-lines" />
            </div>

            <div className="space-y-1.5">
              <Label>Terms heading</Label>
              <Input value={b.terms_heading || ""} onChange={(e) => setB({ ...b, terms_heading: e.target.value })} data-testid="terms-heading" />
            </div>

            <div className="space-y-1.5">
              <Label>Terms &amp; conditions <span className="text-slate-400">(one clause per line)</span></Label>
              <Textarea rows={8} value={field("footer_terms")} onChange={(e) => setField("footer_terms", e.target.value)} data-testid="footer-terms" />
            </div>

            <div className="space-y-1.5">
              <Label>Closing line</Label>
              <Input value={b.closing || ""} onChange={(e) => setB({ ...b, closing: e.target.value })} data-testid="closing" />
            </div>

            <div className="flex justify-end border-t border-slate-100 pt-4">
              <Button onClick={save} disabled={saving} data-testid="save-branding-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save branding
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
